import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import AuthShell from "@/components/marketplace/AuthShell";
import { FIELD_STYLES } from "@/pages/LoginPage";
import { useAuth } from "@/contexts/AuthContext";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const intended = sp.get("intended_role") === "provider" ? "provider" : "customer";

  const [display_name, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(intended);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      const data = await register({
        email,
        password,
        display_name,
        marketing_opt_in: marketingOptIn,
        intended_role: role,
      });
      toast.success("Account created. Welcome to Gulf Coast Work!");
      if (data?.dev_verification_link) {
        toast.info("Dev: verification link logged. Check /verify-email in your inbox / dev store.");
      }
      navigate(role === "provider" ? "/become-a-provider" : "/", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.error || "Could not create account";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create your account."
      subtitle="One account, three ways to get work done. Switch between customer and provider modes anytime."
    >
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-slate-100" role="tablist">
          {[
            { key: "customer", label: "I want to hire" },
            { key: "provider", label: "I'm a provider" },
          ].map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => setRole(o.key)}
              data-testid={`register-role-${o.key}`}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                role === o.key ? "bg-white text-[#071b22] shadow-sm" : "text-[#252A34]/70"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>

        <Field label="Full name">
          <input
            type="text"
            required
            value={display_name}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="How should we call you?"
            data-testid="register-name"
            className="input-primary"
          />
        </Field>
        <Field label="Email">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="register-email"
            className="input-primary"
          />
        </Field>
        <Field label="Password">
          <div className="relative">
            <input
              type={showPass ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              data-testid="register-password"
              className="input-primary pr-11"
            />
            <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#252A34]/50 hover:text-[#165a71]" aria-label="Toggle password">
              {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </Field>

        <label className="inline-flex items-start gap-2 text-sm text-[#252A34]/80">
          <input
            type="checkbox"
            checked={marketingOptIn}
            onChange={(e) => setMarketingOptIn(e.target.checked)}
            data-testid="register-marketing-opt-in"
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#165a71] focus:ring-[#165a71]"
          />
          <span>Send me tips & seasonal offers (unsubscribe anytime).</span>
        </label>

        <button
          type="submit"
          disabled={loading}
          data-testid="register-submit"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#165a71] text-white px-6 py-3 font-medium hover:bg-[#11485a] disabled:opacity-60 transition-colors"
        >
          {loading ? "Creating…" : (<>Create account <ArrowRight className="h-4 w-4" /></>)}
        </button>

        <p className="text-sm text-center text-[#252A34]/70 pt-2">
          Already have one?{" "}
          <Link to="/login" className="font-medium text-[#165a71]" data-testid="register-to-login-link">
            Sign in
          </Link>
        </p>
      </form>
      <style>{FIELD_STYLES}</style>
    </AuthShell>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium text-[#071b22]">{label}</span>
    {children}
  </label>
);
